import { z } from 'zod';

/**
 * The ordered steps of the member profile-completion wizard (IMM-271.2). Values
 * are English and lowercase to mirror the persisted `member_onboarding.current_step`
 * column and the backend `MemberOnboardingStep` union (a deliberate convention
 * exception, like the onboarding-status enum).
 */
export const MemberOnboardingStepSchema = z.enum(['photo_intro', 'contact', 'expertise', 'consent']);

export type MemberOnboardingStep = z.infer<typeof MemberOnboardingStepSchema>;

export const MemberOnboardingStatusSchema = z.enum(['pending', 'in_progress', 'completed']);

export type MemberOnboardingStatus = z.infer<typeof MemberOnboardingStatusSchema>;

/**
 * The revision-safe consent record returned on the onboarding read model: the
 * granted flag plus the server-set timestamp, the granting member's e-mail, and
 * the wording version (IMM-271.2). Null until the wizard is completed.
 */
export const MemberOnboardingConsentSchema = z.object({
  granted: z.boolean(),
  grantedAt: z.iso.datetime(),
  grantedBy: z.email(),
  version: z.string().min(1),
});

export type MemberOnboardingConsent = z.infer<typeof MemberOnboardingConsentSchema>;

export const MemberOnboardingPathParamsSchema = z.object({ workspaceId: z.uuid() });

export const GetMemberOnboardingResponseSchema = z.object({
  status: MemberOnboardingStatusSchema,
  currentStep: MemberOnboardingStepSchema.nullable(),
  consent: MemberOnboardingConsentSchema.nullable(),
});

export type GetMemberOnboardingResponse = z.infer<typeof GetMemberOnboardingResponseSchema>;

export const AdvanceMemberOnboardingStepBodySchema = z.object({ currentStep: MemberOnboardingStepSchema }).strict();

export type AdvanceMemberOnboardingStepBody = z.infer<typeof AdvanceMemberOnboardingStepBodySchema>;

export const AdvanceMemberOnboardingStepResponseSchema = z.object({
  status: MemberOnboardingStatusSchema,
  currentStep: MemberOnboardingStepSchema.nullable(),
});

export type AdvanceMemberOnboardingStepResponse = z.infer<typeof AdvanceMemberOnboardingStepResponseSchema>;

// The completion *input* the client may send: the granted flag + the wording
// version it presented. `grantedAt` / `grantedBy` are stamped server-side and
// never accepted here.
export const CompleteMemberOnboardingBodySchema = z
  .object({
    consent: z.object({ granted: z.boolean(), version: z.string().min(1) }).strict(),
  })
  .strict();

export type CompleteMemberOnboardingBody = z.infer<typeof CompleteMemberOnboardingBodySchema>;

export const CompleteMemberOnboardingResponseSchema = z.object({
  status: MemberOnboardingStatusSchema,
  completedAt: z.iso.datetime(),
});

export type CompleteMemberOnboardingResponse = z.infer<typeof CompleteMemberOnboardingResponseSchema>;

// ---------------------------------------------------------------------------
// Member-invite magic-link + WorkOS OTP session exchange (IMM-271).
//
// An invited member opens the workspace-branded invitation e-mail and lands on
// the public `/onboarding/member?token=<rawToken>` web route. The token is the
// IMM-2 invitation primitive's magic-link token (14-day reusable, hash-only at
// rest). The two-step exchange (ADR 0030, mirroring the partner flow) turns it
// into a real WorkOS session with no password set: step 1 verifies the token
// hash-only and asks WorkOS to e-mail a one-time Magic Auth code; step 2 redeems
// that code, activating the invited user and accepting their pending invitation
// (which initiates the onboarding gate). After step 2 the member rides the
// standard bearer pipeline and the existing onboarding gate routes them to the
// profile wizard.
// ---------------------------------------------------------------------------

// Step 1: body carries the raw magic-link token; the handler verifies it
// hash-only and asks WorkOS to e-mail a one-time code to the invitee. The raw
// token is verified here only — never stored, logged, or returned.
export const RequestMemberInviteSessionCodeBodySchema = z.object({
  token: z.string().trim().min(1),
});

export type RequestMemberInviteSessionCodeBody = z.infer<typeof RequestMemberInviteSessionCodeBodySchema>;

// Step 1 response: the inviting workspace's name, so the code-entry screen can
// front it before any session exists. No access token — the code has only just
// been e-mailed, not redeemed yet.
export const RequestMemberInviteSessionCodeResponseSchema = z.object({
  workspaceName: z.string().min(1),
});

export type RequestMemberInviteSessionCodeResponse = z.infer<typeof RequestMemberInviteSessionCodeResponseSchema>;

// Step 2: body carries the same magic-link token plus the one-time code WorkOS
// e-mailed in step 1. The handler re-verifies the token hash and redeems the
// code for a real WorkOS session.
export const ExchangeMemberInviteSessionBodySchema = z.object({
  token: z.string().trim().min(1),
  code: z.string().trim().min(1),
});

export type ExchangeMemberInviteSessionBody = z.infer<typeof ExchangeMemberInviteSessionBodySchema>;

// Step 2 response: the WorkOS access token (kept in memory by the SPA). The
// refresh token is sealed into the standard `__Secure-immoos_refresh` cookie by
// the presenter and never appears in the body — mirrors `/v1/auth/callback`. No
// onboarding id is returned: the member enters the app and the standard
// onboarding gate routes them to the wizard.
export const ExchangeMemberInviteSessionResponseSchema = z.object({
  accessToken: z.string().min(1),
});

export type ExchangeMemberInviteSessionResponse = z.infer<typeof ExchangeMemberInviteSessionResponseSchema>;
